import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import type { createBrowserRouter as createBrowserRouterFunction } from 'react-router-dom';

type CreateBrowserRouter = typeof createBrowserRouterFunction;

interface MonitoringIdentity {
  userId: string | null;
  organizationId: string | null;
  role: string | null;
}

interface CaptureOptions {
  tags?: Record<string, string | number | boolean | null | undefined>;
  extra?: Record<string, unknown>;
}

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /\b(?:\+?61|0)[2-478](?:[\s-]?\d){8}\b/g;

const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim();
const sentryEnabled =
  Boolean(sentryDsn) &&
  import.meta.env.VITE_SENTRY_ENABLED !== 'false' &&
  (import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true');

let initialized = false;

const ASSET_RELOAD_ATTEMPT_LIMIT = 1;
const ASSET_RELOAD_PARAM = 'worklynx_refresh';
const ASSET_RELOAD_REASON_PARAM = 'worklynx_recovery';
const ASSET_RELOAD_KEY_PREFIX = 'worklynx:asset-reload';
const ASSET_RECOVERY_STABLE_BOOT_DELAY_MS = 10_000;

export function initMonitoring() {
  if (!sentryEnabled || initialized) {
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [
      /^https:\/\/worklynx-v-2\.vercel\.app/,
      /^http:\/\/localhost:\d+/,
      /^http:\/\/127\.0\.0\.1:\d+/,
    ],
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.05),
    beforeSend: sanitizeSentryEvent,
  });

  initialized = true;
}

export function installAssetLoadRecovery() {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    if (recoverFromAssetLoadError(event.error ?? event.message)) {
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (recoverFromAssetLoadError(event.reason)) {
      event.preventDefault();
    }
  });
}

export function scheduleAssetLoadRecoveryCompletion() {
  if (typeof window === 'undefined') return;

  window.setTimeout(() => {
    const recoveredUrl = buildRecoveredApplicationUrl(window.location.href);
    if (recoveredUrl !== window.location.href) {
      window.history.replaceState(window.history.state, '', recoveredUrl);
    }
  }, ASSET_RECOVERY_STABLE_BOOT_DELAY_MS);
}

export function isMonitoringEnabled() {
  return sentryEnabled;
}

export function createMonitoredBrowserRouter(createBrowserRouter: CreateBrowserRouter) {
  if (!sentryEnabled) {
    return createBrowserRouter;
  }

  return Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
}

export function setMonitoringIdentity({ userId, organizationId, role }: MonitoringIdentity) {
  if (!sentryEnabled || !initialized) {
    return;
  }

  Sentry.setUser(userId ? { id: userId } : null);
  Sentry.setTags({
    worklynx_role: role ?? 'none',
    worklynx_organization_id: organizationId ?? 'none',
  });
  Sentry.setContext('worklynx', {
    organization_id: organizationId,
    role,
  });
}

export function captureApplicationError(error: unknown, options: CaptureOptions = {}) {
  if (!sentryEnabled || !initialized) {
    return;
  }

  Sentry.withScope((scope) => {
    Object.entries(options.tags ?? {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        scope.setTag(key, value);
      }
    });

    Object.entries(options.extra ?? {}).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    Sentry.captureException(error);
  });
}

export function recoverFromAssetLoadError(error: unknown): boolean {
  if (typeof window === 'undefined' || !isRecoverableAssetLoadError(error)) {
    return false;
  }

  const recoveryWindow = window as Window & { __worklynxAssetReloadAttempted?: boolean };
  if (recoveryWindow.__worklynxAssetReloadAttempted) {
    return false;
  }

  const reloadKey = getAssetReloadKey();
  const attempt = reserveAssetReloadAttempt(reloadKey);
  if (!attempt) {
    return false;
  }

  recoveryWindow.__worklynxAssetReloadAttempted = true;
  forceAssetRecoveryReload('asset-load-retry');

  return true;
}

export function forceAssetRecoveryReload(reason = 'manual') {
  if (typeof window === 'undefined') return;
  window.location.replace(buildAssetRecoveryUrl(window.location.href, Date.now(), reason));
}

export function buildAssetRecoveryUrl(
  currentHref: string,
  timestamp: number | string,
  reason = 'manual',
) {
  const url = new URL(currentHref);
  url.searchParams.set(ASSET_RELOAD_PARAM, String(timestamp));
  url.searchParams.set(ASSET_RELOAD_REASON_PARAM, reason);
  return url.toString();
}

export function buildRecoveredApplicationUrl(currentHref: string) {
  const url = new URL(currentHref);
  url.searchParams.delete(ASSET_RELOAD_PARAM);
  url.searchParams.delete(ASSET_RELOAD_REASON_PARAM);
  return url.toString();
}

export function isRecoverableAssetLoadError(error: unknown): boolean {
  const message = getErrorText(error).toLowerCase();
  if (!message) return false;

  return ASSET_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export function sanitizeSentryEvent(event: ErrorEvent): ErrorEvent | null {
  if (event.request) {
    event.request.url = stripQueryString(event.request.url);
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.query_string;
  }

  if (event.user) {
    event.user = event.user.id ? { id: event.user.id } : undefined;
  }

  if (event.message) {
    event.message = redactText(event.message);
  }

  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = redactText(exception.value);
    }
  });

  return event;
}

export function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(1, Math.max(0, parsed));
}

function redactText(value: string) {
  return value
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(PHONE_PATTERN, '[redacted-phone]');
}

function stripQueryString(url: string | undefined) {
  if (!url) {
    return url;
  }

  const [base] = url.split('?');
  return base;
}

const ASSET_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'chunkloaderror',
  'loading chunk',
  'failed to load module script',
  'expected a javascript-or-wasm module script',
];

function getErrorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const maybeError = error as { name?: unknown; message?: unknown; reason?: unknown };
    return [maybeError.name, maybeError.message, maybeError.reason]
      .filter((value): value is string => typeof value === 'string')
      .join(' ');
  }

  return '';
}

function getAssetReloadKey() {
  const entryScript = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return buildAssetReloadKey(
    window.location.pathname,
    import.meta.env.VITE_SENTRY_RELEASE,
    entryScript?.src,
    import.meta.env.VITE_APP_ENV,
  );
}

export function buildAssetReloadKey(
  pathname: string,
  release: string | undefined,
  entryScriptUrl: string | undefined,
  environment = 'default',
) {
  const entryFingerprint = entryScriptUrl
    ? entryScriptUrl.split('?')[0]?.split('/').pop()
    : undefined;
  const fingerprint = entryFingerprint || release?.trim() || environment || 'default';
  return `${ASSET_RELOAD_KEY_PREFIX}:${fingerprint}:${pathname}`;
}

function reserveAssetReloadAttempt(reloadKey: string) {
  try {
    pruneStaleAssetReloadAttempts(reloadKey);
    const currentAttempt = Number(window.sessionStorage.getItem(reloadKey) ?? '0');
    const safeCurrentAttempt = Number.isFinite(currentAttempt) ? currentAttempt : 0;
    if (safeCurrentAttempt >= ASSET_RELOAD_ATTEMPT_LIMIT) {
      return null;
    }

    const nextAttempt = safeCurrentAttempt + 1;
    window.sessionStorage.setItem(reloadKey, String(nextAttempt));
    return nextAttempt;
  } catch {
    // Some privacy modes block storage. The in-memory flag still prevents
    // duplicate reload attempts during the current page lifetime.
    return 1;
  }
}

function pruneStaleAssetReloadAttempts(activeKey: string) {
  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(`${ASSET_RELOAD_KEY_PREFIX}:`) && key !== activeKey) {
      window.sessionStorage.removeItem(key);
    }
  }
}
